// Estado compartido del espacio de una cotización: el detalle (registro + versiones + historial), la revisión vigente
// y una sola forma de mandar cambios. Cada cambio lleva la revisión que la persona vio; si otra persona guardó antes,
// el servidor responde CONFLICTO y aquí se levanta una franja que obliga a recargar (no se pisa nada en silencio).
//
// Bug de producción del 12-oct (Diego subió 12 recibos seguidos, tocó «Leer» y recibió 409 en 0.4 s sin que nadie más
// tocara la cotización): después de cada acción se pedía el detalle «por detrás», y una de esas respuestas que llegaba
// TARDE (pedida antes de la subida siguiente) volvía a poner en pantalla una revisión vieja. Ahora:
//   - la revisión en pantalla NUNCA va hacia atrás: una respuesta vieja que llega tarde se ignora;
//   - el detalle por detrás se pide una sola vez al final de una ráfaga de acciones (12 subidas = 1 detalle, no 12);
//   - las acciones que no pisan nada (subir, leer, quitar un archivo, ediciones con su «antes», que el servidor revisa)
//     recargan y reintentan SOLAS una vez si el servidor dice que la cotización cambió;
//   - la lectura de recibos es UNA a la vez y arranca sola al terminar de subir recibos (lectura, abajo).
// Verificación adversarial de 9e40380 (12-oct):
//   - el 409 del CANDADO («ya hay una operación en curso»: otra pestaña u otro teléfono está leyendo o sacando el PDF)
//     ya no levanta la franja de «la cotización cambió» (no cambió nada): la lectura ESPERA a que termine y lee después;
//   - si una subida tuvo problemas (un archivo repetido, uno que no es PDF ni foto, uno que no se subió), la lectura
//     automática lee lo que sí se subió pero NO salta a Revisión: los problemas se quedan a la vista en Recibos.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Accion, Cotizacion, DetalleCotizacion } from '@cotizador/compartido';
import { api, cotizacionDe } from '../../api/consultas.ts';
import { ErrorDeApi } from '../../api/cliente.ts';
import { navegar } from '../../ruteo.tsx';
import { usarUsuario } from '../../sesion.tsx';
import { recibosSinLeer } from '../../logica/archivos-leidos.ts';

/** Cuánto se espera, tras la última acción, para pedir el detalle (historial y versiones) por detrás. */
export const ESPERA_REFRESCO_MS = 250;

/** Si la cotización está OCUPADA (409 OPERACION_EN_CURSO: otra pestaña u otro teléfono lee los recibos o saca el PDF),
 *  la lectura vuelve a intentar cada `cadaMs` hasta `maximoMs` (el candado del servidor vence a los 3 min sin
 *  renovarse). Las pruebas lo acortan. */
export const ESPERA_OCUPADA = { cadaMs: 3_000, maximoMs: 3 * 60_000 };

export interface OpcionesEjecutar {
  /** Si el servidor responde que la cotización cambió (409 CONFLICTO), se recarga y se intenta UNA vez más con la
   *  revisión nueva. true = siempre; una función decide con la cotización recién cargada (p. ej. autorizar la tabla: solo
   *  si la tabla sigue siendo la que la persona vio). Solo para acciones que no pueden pisar el cambio de otro. */
  reintentar?: boolean | ((nueva: Cotizacion) => boolean);
}

export interface LecturaRecibos {
  /** Hay una lectura en curso (con o sin IA). */
  leyendo: boolean;
  /** La lectura espera a que termine otra operación de esta cotización (otra pestaña u otro teléfono). */
  esperandoOtra: boolean;
  /** Hay recibos subiéndose (la lectura arranca sola cuando terminan). */
  subiendo: boolean;
  error: unknown;
  limpiarError: () => void;
  /** Lee los recibos. UNA a la vez: si ya se está leyendo, al terminar se lee otra vez solo si quedaron recibos sin leer
   *  (nunca dos lecturas juntas ni una lectura pagada dos veces). true = leyó. */
  leer: () => Promise<boolean>;
  /** El subidor avisa que empieza y termina de subir; al terminar la última subida con recibos, la lectura arranca sola.
   *  `esperarLectura` deja para después de la lectura en curso lo que se elige mientras tanto (subir a media lectura
   *  hacía que la lectura terminara en 409 y se perdiera lo pagado). */
  empezarSubida: () => Promise<void>;
  /** `conProblemas`: algún archivo no se subió (repetido, tipo, peso, error): la lectura automática lee lo que sí se
   *  subió, pero la pantalla se queda en Recibos para que los problemas se vean. */
  terminarSubida: (subioRecibos: boolean, conProblemas?: boolean) => void;
}

export interface ValorCotizacion {
  detalle: DetalleCotizacion;
  cot: Cotizacion;
  conflicto: boolean;
  recargar: () => Promise<void>;
  /** Corre una acción con la cotización vigente. Si la respuesta trae la cotización nueva, se usa; el detalle se refresca
   *  por detrás. Los errores se lanzan (la pantalla los enseña junto a su botón). */
  ejecutar: <T>(accion: (c: Cotizacion) => Promise<T>, op?: OpcionesEjecutar) => Promise<T>;
  lectura: LecturaRecibos;
}

const Contexto = createContext<ValorCotizacion | null>(null);

export function usarCotizacion(): ValorCotizacion {
  const v = useContext(Contexto);
  if (!v) throw new Error('usarCotizacion fuera de <ProveedorCotizacion>');
  return v;
}

/** ¿Puede la persona hacer `accion` en ESTA cotización? Rol + política "vendedor externo edita solo lo suyo"
 *  (contrato v2, puedeEnCotizacion). Toda sección del espacio pregunta aquí, nunca solo por el rol. */
export function usarPuedeAqui(): (accion: Accion) => boolean {
  const { cot } = usarCotizacion();
  const { puedeEn } = usarUsuario();
  return (accion) => puedeEn(accion, cot);
}

export function ProveedorCotizacion({
  id,
  children,
  cargando,
  alError,
}: {
  id: string;
  children: ReactNode;
  cargando: ReactNode;
  alError: (e: unknown, reintentar: () => void) => ReactNode;
}) {
  const { puedeEn } = usarUsuario();
  const [detalle, setDetalle] = useState<DetalleCotizacion | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [conflicto, setConflicto] = useState(false);
  /** Lo vigente, al instante (el estado de React llega en el siguiente render; las acciones en fila no pueden esperarlo). */
  const vigente = useRef<DetalleCotizacion | null>(null);
  const idVigente = useRef(id);
  idVigente.current = id;

  const poner = useCallback((d: DetalleCotizacion | null) => {
    vigente.current = d;
    setDetalle(d);
  }, []);

  /** Pone un detalle que llegó del servidor SIN ir nunca hacia atrás: uno que se pidió antes de la última acción y llega
   *  tarde trae una revisión vieja y se ignora. false = se ignoró. */
  const aplicarDetalle = useCallback(
    (d: DetalleCotizacion): boolean => {
      if (d.cotizacion.id !== idVigente.current) return false;
      const v = vigente.current;
      if (v && v.cotizacion.id === d.cotizacion.id && d.cotizacion.revision < v.cotizacion.revision) return false;
      poner(d);
      return true;
    },
    [poner],
  );

  /** La cotización que devolvió una acción (con el historial de antes: el nuevo llega por detrás). */
  const aplicarCotizacion = useCallback(
    (c: Cotizacion) => {
      const v = vigente.current;
      if (!v || c.id !== v.cotizacion.id || c.revision < v.cotizacion.revision) return;
      poner({ ...v, cotizacion: c });
    },
    [poner],
  );

  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** El historial y las versiones se refrescan por detrás, UNA vez al final de una ráfaga de acciones. */
  const refrescarDetras = useCallback(() => {
    if (temporizador.current) clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => {
      temporizador.current = null;
      api
        .detalle(idVigente.current)
        .then(aplicarDetalle)
        .catch(() => undefined);
    }, ESPERA_REFRESCO_MS);
  }, [aplicarDetalle]);
  useEffect(
    () => () => {
      if (temporizador.current) clearTimeout(temporizador.current);
    },
    [],
  );

  const recargar = useCallback(async () => {
    try {
      const d = await api.detalle(id);
      aplicarDetalle(d);
      setError(null);
      setConflicto(false);
    } catch (e) {
      if (!vigente.current) setError(e);
      else throw e;
    }
  }, [id, aplicarDetalle]);

  useEffect(() => {
    poner(null);
    void recargar();
  }, [recargar, poner]);

  const ejecutar = useCallback(
    async <T,>(accion: (c: Cotizacion) => Promise<T>, op: OpcionesEjecutar = {}): Promise<T> => {
      const una = async (): Promise<T> => {
        const d = vigente.current;
        if (!d) throw new Error('La cotización todavía no carga.');
        const r = await accion(d.cotizacion);
        const nueva = cotizacionDe(r);
        if (nueva) aplicarCotizacion(nueva);
        refrescarDetras();
        return r;
      };
      try {
        return await una();
      } catch (e) {
        if (!(e instanceof ErrorDeApi && e.esConflicto)) throw e;
        if (op.reintentar && e.codigo === 'CONFLICTO') {
          // La cotización sí cambió (otra pestaña, otro teléfono, otra persona): se recarga y se intenta una vez más,
          // sin perder lo que la persona hizo (la acción se vuelve a armar con la cotización nueva).
          let recargada = false;
          try {
            const d = await api.detalle(idVigente.current);
            aplicarDetalle(d);
            recargada = true;
          } catch {
            /* sin red: se enseña el 409 */
          }
          const sigue = recargada && vigente.current && (op.reintentar === true || op.reintentar(vigente.current.cotizacion));
          if (sigue) {
            try {
              return await una();
            } catch (e2) {
              if (e2 instanceof ErrorDeApi && e2.esConflicto) setConflicto(true);
              throw e2;
            }
          }
        }
        setConflicto(true);
        throw e;
      }
    },
    [aplicarCotizacion, aplicarDetalle, refrescarDetras],
  );

  // ── Lectura de recibos: una a la vez, y sola al terminar de subir ──
  const [leyendo, setLeyendo] = useState(false);
  const [esperandoOtra, setEsperandoOtra] = useState(false);
  const [subiendo, setSubiendo] = useState(0);
  const [errorLectura, setErrorLectura] = useState<unknown>(null);
  const enCurso = useRef<Promise<boolean> | null>(null);
  const otraVuelta = useRef(false);
  const subidas = useRef(0);
  const pendiente = useRef(false);
  /** Una subida tuvo problemas: la próxima lectura no salta a Revisión (los problemas se quedan a la vista). */
  const quedarseEnRecibos = useRef(false);

  /** Una lectura; si la cotización está ocupada por otra operación (candado del servidor), espera y vuelve a intentar. */
  const leerEsperando = useCallback(async () => {
    const hasta = Date.now() + ESPERA_OCUPADA.maximoMs;
    try {
      for (;;) {
        try {
          return await ejecutar((c) => api.leerRecibos(c.id, c.revision), { reintentar: true });
        } catch (e) {
          if (!(e instanceof ErrorDeApi && e.codigo === 'OPERACION_EN_CURSO') || Date.now() + ESPERA_OCUPADA.cadaMs > hasta) throw e;
          setEsperandoOtra(true);
          await new Promise((r) => setTimeout(r, ESPERA_OCUPADA.cadaMs));
        }
      }
    } finally {
      setEsperandoOtra(false);
    }
  }, [ejecutar]);

  const leer = useCallback((): Promise<boolean> => {
    if (enCurso.current) {
      otraVuelta.current = true;
      return enCurso.current;
    }
    const p = (async () => {
      setLeyendo(true);
      setErrorLectura(null);
      let leyo = false;
      try {
        do {
          otraVuelta.current = false;
          await leerEsperando();
          leyo = true;
          // Otra vuelta solo si de verdad quedaron recibos sin leer (nunca se paga dos veces la misma lectura).
        } while (otraVuelta.current && vigente.current && recibosSinLeer(vigente.current.cotizacion.borrador).length > 0);
      } catch (e) {
        setErrorLectura(e);
      } finally {
        enCurso.current = null;
        otraVuelta.current = false;
        setLeyendo(false);
      }
      // Leído desde Recibos: lo que sigue es revisar y autorizar la tabla (salvo que haya recibos esperando subirse: esos se
      // suben y se leen, y entonces sí; o que la subida haya tenido problemas: esos se quedan a la vista en Recibos).
      const quedarse = quedarseEnRecibos.current;
      if (subidas.current === 0) quedarseEnRecibos.current = false;
      if (leyo && !quedarse && subidas.current === 0 && typeof window !== 'undefined' && window.location.pathname === `/cotizaciones/${idVigente.current}/recibos`)
        navegar(`/cotizaciones/${idVigente.current}/revision`);
      return leyo;
    })();
    enCurso.current = p;
    return p;
  }, [leerEsperando]);

  const empezarSubida = useCallback(async () => {
    subidas.current++;
    setSubiendo(subidas.current);
    // Lo que se elige mientras se lee se sube al terminar la lectura (si no, la lectura terminaba en 409).
    if (enCurso.current) await enCurso.current.catch(() => false);
  }, []);

  const puedeLeer = detalle ? puedeEn('editar_cotizaciones', detalle.cotizacion) : false;
  const puedeLeerRef = useRef(puedeLeer);
  puedeLeerRef.current = puedeLeer;
  const terminarSubida = useCallback(
    (subioRecibos: boolean, conProblemas = false) => {
      subidas.current = Math.max(0, subidas.current - 1);
      setSubiendo(subidas.current);
      if (subioRecibos) pendiente.current = true;
      if (conProblemas) quedarseEnRecibos.current = true;
      if (subidas.current > 0) return;
      const v = vigente.current;
      // Lectura automática: al terminar de subir recibos (la última subida de la ráfaga), si quien sube puede leer, la
      // cotización espera recibos y hay alguno sin leer.
      const leerAhora =
        pendiente.current && !!v && puedeLeerRef.current && !v.cotizacion.borrador.cuestionario.consumo.es_nuevo && recibosSinLeer(v.cotizacion.borrador).length > 0;
      pendiente.current = false;
      if (leerAhora) void leer();
      // Sin lectura que lo use, el «quedarse en Recibos» no se arrastra a una lectura que alguien pida después.
      else if (!enCurso.current) quedarseEnRecibos.current = false;
    },
    [leer],
  );

  if (error) return <>{alError(error, () => void recargar())}</>;
  if (!detalle) return <>{cargando}</>;
  const lectura: LecturaRecibos = {
    leyendo,
    esperandoOtra,
    subiendo: subiendo > 0,
    error: errorLectura,
    limpiarError: () => setErrorLectura(null),
    leer,
    empezarSubida,
    terminarSubida,
  };
  return <Contexto.Provider value={{ detalle, cot: detalle.cotizacion, conflicto, recargar, ejecutar, lectura }}>{children}</Contexto.Provider>;
}

/** PLAN-NOCHE §2b (criterio 3): "cada cambio crea una versión nueva con historial y regenera el PDF". Después de un cambio
 *  de precio (costo adicional, súbele/bájale) en una cotización que YA tiene PDF, se genera la versión siguiente sola,
 *  con el cambio como motivo. Si no se puede sin que alguien decida (un aviso rojo que pide nota, el OK de Diego, una
 *  falla del formato), no se fuerza: se dice por qué y la franja "el PDF ya no dice lo último" lleva a la sección PDF. */
export type ResultadoRegenerar = { tipo: 'sin_pdf' } | { tipo: 'regenerado'; numero: number } | { tipo: 'pendiente'; motivo: string };

export function usarRegenerarPdf(): (motivo: string) => Promise<ResultadoRegenerar> {
  const { detalle, ejecutar } = usarCotizacion();
  const { puedeEn } = usarUsuario();
  const tienePdf = detalle.versiones.some((v) => v.pdf);
  return async (motivo) => {
    if (!tienePdf) return { tipo: 'sin_pdf' };
    try {
      const r = await ejecutar((c) => {
        if (!puedeEn('editar_cotizaciones', c)) throw new Error('Solo su vendedor o Diego generan el PDF de esta cotización.');
        if (c.borrador.avisos.some((a) => a.nivel === 'rojo' && !a.resuelto)) throw new Error('Hay avisos rojos que piden tu nota: genera el PDF desde la sección PDF.');
        return api.generarPdf(c.id, c.revision, motivo.slice(0, 300), []);
      });
      return { tipo: 'regenerado', numero: r.version.numero };
    } catch (e) {
      return { tipo: 'pendiente', motivo: e instanceof Error ? e.message : String(e) };
    }
  };
}
